import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useFormikContext } from 'formik';

import { pluralize } from '@/react/common/string-utils';

import { FormControl } from '@@/form-components/FormControl';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { FormSection } from '@@/form-components/FormSection';
import { SwitchField } from '@@/form-components/SwitchField';
import { Input, Select } from '@@/form-components/Input';

import { RegistrySelector } from '../../RegistrySelector/RegistrySelector';
import { NamespaceSelect } from '../rbac/NamespaceSelect';

import { KubernetesRegistryAccessDatatable } from './KubernetesRegistryAccessDatatable';
import { AllowedSourcesDatatable } from './AllowedSourcesDatatable';
import {
  AllowedSourceScope,
  DEFAULT_ALLOWED_SOURCES,
  KubernetesRegistryFormValues,
} from './types';

interface Props {
  mode: 'create' | 'edit';
}

export function KubernetesRegistryFormFields({ mode }: Props) {
  const { values, setFieldValue, setValues } =
    useFormikContext<KubernetesRegistryFormValues>();
  const [tempSelectedRegistry, setTempSelectedRegistry] = useState<
    number | null
  >(null);
  const [tempSelectedNamespaces, setTempSelectedNamespaces] = useState<
    string[]
  >([]);

  // Allowed source form state
  const [tempSourceUrl, setTempSourceUrl] = useState('');
  const [tempSourceUrlError, setTempSourceUrlError] = useState('');
  const [tempSourceScope, setTempSourceScope] =
    useState<AllowedSourceScope>('global');
  const [tempSourceNamespaces, setTempSourceNamespaces] = useState<string[]>(
    []
  );

  const canAddAccess =
    tempSelectedRegistry !== null && tempSelectedNamespaces.length > 0;

  const canAddSource =
    tempSourceUrl.trim() !== '' &&
    (tempSourceScope === 'global' || tempSourceNamespaces.length > 0);

  return (
    <>
      <FormSection title="Add access">
        <FormControl label="Select registry" required>
          <RegistrySelector
            value={tempSelectedRegistry}
            onChange={setTempSelectedRegistry}
            filter={(registries) => registries.filter((r) => r.Id !== 0)}
            isClearable
          />
        </FormControl>

        <FormControl label="Namespaces" required>
          <p className="mb-2 text-xs text-muted">
            Add the namespaces that should have access to the registry:
          </p>
          <NamespaceSelect
            selectedNamespaces={tempSelectedNamespaces}
            onChange={setTempSelectedNamespaces}
            placeholder="e.g. default, production"
            data-cy="registry-namespaces-input"
            namespaces={getNamespacesFromAccessList()}
          />
        </FormControl>

        <div className="form-group">
          <div className="col-sm-12 flex gap-2">
            <Button
              icon={Plus}
              color="primary"
              size="small"
              disabled={!canAddAccess}
              onClick={handleAddAccess}
              data-cy="add-registry-access-button"
              className="!ml-0"
            >
              Add Access
            </Button>
          </div>
        </div>
      </FormSection>

      <div className="-mx-4">
        <KubernetesRegistryAccessDatatable />
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <RegistryAccessInfoMessage
            registriesAddedCount={values.accessRows?.length ?? 0}
            mode={mode}
          />
        </div>
      </div>

      <FormSection title="Container image restrictions">
        <div className="form-group">
          <div className="col-sm-12">
            <SwitchField
              label="Restrict to allowed sources"
              labelClass="col-sm-3 col-lg-2"
              checked={values.restrictSources || false}
              onChange={handleRestrictSourcesToggle}
              data-cy="restrict-sources-toggle"
            />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-9 col-lg-10 col-sm-offset-3 col-lg-offset-2 mb-2">
            <TextTip color="blue" inline={false}>
              Enforce that only container images from allowed sources can be
              deployed. This applies to all containers, including init and
              ephemeral containers by creating a ValidatingAdmissionPolicy
              resource that is checked before pods are created. Requires
              Kubernetes 1.30 or later.
            </TextTip>
          </div>
        </div>
        {values.restrictSources && (
          <>
            <FormSection title="Add allowed source">
              <FormControl
                label="Registry URL prefix"
                required
                errors={tempSourceUrlError}
              >
                <Input
                  value={tempSourceUrl}
                  onChange={(e) => {
                    setTempSourceUrl(e.target.value);
                    setTempSourceUrlError('');
                  }}
                  placeholder="e.g. docker.io/myorg, gcr.io/myproject, nginx, myorg/myapp"
                  data-cy="allowed-source-url-input"
                />
              </FormControl>

              <FormControl label="Scope">
                <Select
                  value={tempSourceScope}
                  onChange={(e) =>
                    setTempSourceScope(e.target.value as AllowedSourceScope)
                  }
                  options={[
                    { label: 'Global', value: 'global' },
                    {
                      label: 'Specific namespaces',
                      value: 'namespaces',
                    },
                  ]}
                  data-cy="allowed-source-scope-select"
                />
              </FormControl>

              {tempSourceScope === 'namespaces' && (
                <FormControl label="Namespaces" required>
                  <NamespaceSelect
                    selectedNamespaces={tempSourceNamespaces}
                    onChange={setTempSourceNamespaces}
                    placeholder="e.g. default, production"
                    data-cy="allowed-source-namespaces-input"
                    namespaces={[]}
                  />
                </FormControl>
              )}

              <div className="form-group">
                <div className="col-sm-12 flex gap-2">
                  <Button
                    icon={Plus}
                    color="primary"
                    size="small"
                    disabled={!canAddSource}
                    onClick={handleAddSource}
                    data-cy="add-allowed-source-button"
                    className="!ml-0"
                  >
                    Add Source
                  </Button>
                </div>
              </div>
            </FormSection>

            <div className="-mx-4">
              <AllowedSourcesDatatable />
            </div>
          </>
        )}
      </FormSection>
    </>
  );

  function handleRestrictSourcesToggle(value: boolean) {
    if (
      value &&
      (!values.allowedSources || values.allowedSources.length === 0)
    ) {
      // Batch both updates so validation sees the complete state.
      setValues({
        ...values,
        restrictSources: value,
        allowedSources: [...DEFAULT_ALLOWED_SOURCES],
      });
    } else {
      setFieldValue('restrictSources', value);
    }
  }

  function handleAddSource() {
    if (!canAddSource) return;

    const cleanedUrl = tempSourceUrl.trim().replace(/\/+$/, '');

    // "docker.io" alone is too broad — users must specify a path component
    // (e.g. docker.io/myorg, docker.io/library/nginx) so the policy can
    // generate meaningful image-matching rules.
    if (cleanedUrl === 'docker.io') {
      setTempSourceUrlError(
        'Please specify a path, e.g. docker.io/myorg or docker.io/library'
      );
      return;
    }
    const cleanedNamespaces =
      tempSourceScope === 'namespaces'
        ? tempSourceNamespaces.map((ns) => ns.trim()).filter((ns) => ns !== '')
        : [];

    const newSources = [...(values.allowedSources || [])];
    const existingIndex = newSources.findIndex(
      (source) => source.registryUrlPrefix === cleanedUrl
    );

    if (existingIndex !== -1) {
      newSources[existingIndex] = {
        ...newSources[existingIndex],
        scope: tempSourceScope,
        namespaces: cleanedNamespaces,
      };
    } else {
      newSources.push({
        registryUrlPrefix: cleanedUrl,
        scope: tempSourceScope,
        namespaces: cleanedNamespaces,
      });
    }

    setFieldValue('allowedSources', newSources);
    setTempSourceUrl('');
    setTempSourceUrlError('');
    setTempSourceScope('global');
    setTempSourceNamespaces([]);
  }

  function handleAddAccess() {
    if (!canAddAccess) return;

    const cleanedNamespaces = tempSelectedNamespaces
      .map((ns) => ns.trim())
      .filter((ns) => ns !== '');

    const newAccessRows = [...(values.accessRows || [])];
    const existingIndex = newAccessRows.findIndex(
      (row) => row.registryId === tempSelectedRegistry
    );

    if (existingIndex !== -1) {
      newAccessRows[existingIndex] = {
        ...newAccessRows[existingIndex],
        namespaces: cleanedNamespaces,
      };
    } else {
      newAccessRows.push({
        id: values.accessRows.length + 1,
        registryId: tempSelectedRegistry as number,
        namespaces: cleanedNamespaces,
      });
    }

    setFieldValue('accessRows', newAccessRows);
    setTempSelectedRegistry(null);
    setTempSelectedNamespaces([]);
  }

  function getNamespacesFromAccessList(): string[] {
    return Array.from(
      new Set((values.accessRows || []).flatMap((a) => a.namespaces || []))
    );
  }
}

function RegistryAccessInfoMessage({
  registriesAddedCount,
  mode,
}: {
  registriesAddedCount: number;
  mode: 'create' | 'edit';
}) {
  return <TextTip color="blue">{getMessage()}</TextTip>;

  function getMessage() {
    if (registriesAddedCount > 0) {
      return `Registry access configured for ${registriesAddedCount} ${pluralize(
        registriesAddedCount,
        'registry',
        'registries'
      )}.`;
    }
    return `Add at least one registry access or enable restrict sources to ${
      mode === 'create' ? 'create' : 'update'
    } this policy.`;
  }
}
